import { FC, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  HStack,
  Icon,
  IconButton,
  Text,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { IconMaximize, IconMinimize, IconCircleCheck, IconRoad, IconArrowsRightLeft } from "@tabler/icons-react";
import { formatCurrency, formatCombustivel, corCombustivel } from "@Utils/index";
import type { PostoDetalhe, ComposicaoPagoItem, ComposicaoPeriodoItem, ProdutoBreakdown } from "@Api/estudo";

const num2 = (n: number) =>
  n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (frac: number) =>
  (frac * 100).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + "%";
// Número puro (unidade fica no cabeçalho da coluna).
const pctNum = (frac: number) =>
  (frac * 100).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const int = (n: number) => n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
const titulo = (s?: string | null) =>
  (s ?? "").toLowerCase().replace(/\b\p{L}/gu, (c) => c.toUpperCase());
const diaBR = (iso: string | null) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("pt-BR") : "—";
const periodo = (a: string | null, b: string | null) =>
  a && b && a !== b ? `${diaBR(a)}–${diaBR(b)}` : diaBR(a ?? b);

const th = { textTransform: "none" as const, fontSize: "11px", color: "neutrals.text.sub-600" };
const td = { fontSize: "12px", verticalAlign: "top" as const };

// Chip do combustível na cor ANP (S-10 cinza / S-500 vermelho).
const ChipCombustivel: FC<{ produto: string }> = ({ produto }) => {
  const c = corCombustivel(produto);
  return (
    <Text
      as="span"
      fontSize="10px"
      fontWeight="700"
      px="6px"
      py="1px"
      borderRadius="full"
      whiteSpace="nowrap"
      color={c.fg}
      bg={c.bg}
      border="1px solid"
      borderColor={c.border}
    >
      {formatCombustivel(produto)}
    </Text>
  );
};

// Rodapé de uma célula de preço: VARIAÇÃO deste preço (pivô = o preço do card) em
// relação ao que o cliente pagou. ▼ abaixo = este preço é menor que o pago (verde =
// oportunidade); ▲ acima = este preço é maior que o pago (neutro, contexto).
type Delta = { pago: number; ref: number | null };

const DeltaLine: FC<{ delta: Delta }> = ({ delta }) => {
  if (delta.ref == null) return null;
  const diff = delta.ref - delta.pago; // >0 = este preço ACIMA do pago; <0 = ABAIXO
  const eps = 0.005;
  if (diff < -eps) {
    return (
      <Text fontSize="10px" fontWeight="600" color="states.success.dark" mt="3px">
        ▼ {formatCurrency(-diff)}/L abaixo
      </Text>
    );
  }
  if (diff > eps) {
    return (
      <Text fontSize="10px" fontWeight="600" color="neutrals.text.sub-600" mt="3px">
        ▲ {formatCurrency(diff)}/L acima
      </Text>
    );
  }
  return (
    <Text fontSize="10px" color="neutrals.text.soft-400" mt="3px">
      no preço
    </Text>
  );
};

// Célula de um preço de referência do posto (usado/acordo/padrão).
const RefCell: FC<{ label: string; value: number | null; hint?: string; accent?: boolean; delta?: Delta }> = ({
  label,
  value,
  hint,
  accent,
  delta,
}) => (
  <Box
    borderRadius="8px"
    border="1px solid"
    borderColor="neutrals.stroke.soft-200"
    bg="neutrals.surface.surface-0"
    px="12px"
    py="10px"
  >
    <Text fontSize="10px" fontWeight="600" textTransform="uppercase" letterSpacing="0.05em" color="neutrals.text.soft-400">
      {label}
    </Text>
    <Text
      fontSize="15px"
      fontWeight="700"
      fontFamily="heading"
      color={accent ? "states.success.dark" : "neutrals.text.strong-950"}
    >
      {value != null ? `${formatCurrency(value)}/L` : "—"}
    </Text>
    {delta && <DeltaLine delta={delta} />}
    {hint && (
      <Text fontSize="10px" color="neutrals.text.soft-400" mt="2px">
        {hint}
      </Text>
    )}
  </Box>
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  posto: PostoDetalhe | null;
}

/**
 * Mostra "o que o preço é de fato" para o posto: a composição do que o cliente
 * PAGOU (média ponderada, aberta preço a preço) e os preços de referência do posto
 * — menor/médio/maior USADO (vendas na janela do arquivo) e menor/médio/maior
 * ACORDO disponível hoje, mais o padrão vigente — com as duas economias.
 */
export const ComposicaoPrecoModal: FC<Props> = ({ isOpen, onClose, posto }) => {
  const [full, setFull] = useState(false);
  const pago = posto?.composicaoPago ?? [];
  // Posto CREDENCIADO com vendas RF → bloco "Preços com venda no Posto".
  const temVendas =
    !!posto && (posto.menorPrecoUsado != null || posto.menorPrecoMedioDiario != null);
  // Posto NÃO credenciado, mas com posto indicado MAIS BARATO por perto → a referência
  // é o preço do indicado (a economia da linha vem daí). Sem isso o modal fica vazio.
  const switchRef =
    posto &&
    !posto.casou &&
    posto.sugestao?.dieselPrice != null &&
    posto.sugestao.dieselPrice < posto.precoMedioPago
      ? posto.sugestao.dieselPrice
      : null;
  const isSwitch = switchRef != null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={full ? "full" : "3xl"} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px" fontFamily="heading">
          Composição do preço
          {(posto?.nome || posto?.cidade) && (
            <Text fontSize="12px" fontWeight="400" color="neutrals.text.sub-600" mt="2px">
              {posto?.nome}
              {posto?.cidade
                ? `${posto?.nome ? " · " : ""}${posto.cidade}${posto.estado ? "/" + posto.estado : ""}`
                : ""}
            </Text>
          )}
        </ModalHeader>
        <IconButton
          aria-label={full ? "Restaurar tamanho" : "Maximizar"}
          icon={<Icon as={full ? IconMinimize : IconMaximize} boxSize={4} />}
          variant="ghost"
          size="sm"
          position="absolute"
          right="40px"
          top="12px"
          onClick={() => setFull((v) => !v)}
        />
        <ModalCloseButton />
        <ModalBody pb="20px">
          {posto && (
            <Box mb="20px">
              {/* Âncora: o preço pago é a BASE da economia. */}
              <HStack justify="space-between" mb="6px" px="12px" py="10px" borderRadius="8px" bg="neutrals.background.weak-50" border="1px solid" borderColor="neutrals.stroke.soft-200">
                <Text fontSize="11px" fontWeight="600" textTransform="uppercase" letterSpacing="0.05em" color="neutrals.text.soft-400">
                  Valor pago pelo Cliente · Média Ponderada
                </Text>
                <Text fontSize="18px" fontWeight="700" fontFamily="heading" color="neutrals.text.strong-950">
                  {formatCurrency(posto.precoMedioPago)}/L
                </Text>
              </HStack>

              {/* Selo: o cliente já paga no piso do posto (só credenciado). */}
              {temVendas && posto.economiaRealista != null && posto.economiaRealista.porLitro <= 0.005 && (
                <HStack spacing={2} mt="10px" mb="4px" px="12px" py="8px" borderRadius="8px" bg="states.success.lighter" border="1px solid" borderColor="states.success.light">
                  <Icon as={IconCircleCheck} boxSize={4} color="states.success.base" />
                  <Text fontSize="12px" fontWeight="600" color="states.success.dark">
                    O cliente tem o menor preço do Posto
                  </Text>
                </HStack>
              )}

              {isSwitch && switchRef != null ? (
                /* NÃO credenciado: a referência é o posto INDICADO mais próximo. */
                <Box mt="12px" borderRadius="8px" border="1px solid" borderColor="states.success.light" bg="states.success.lighter" px="12px" py="10px">
                  <Text fontSize="12px" fontWeight="700" color="states.success.dark">
                    Troca de posto necessária
                  </Text>
                  {/* Mesmo estilo da grid principal: ícone + "Trocar por … · X km" + rodovia. */}
                  <HStack spacing="6px" mt="4px" align="flex-start">
                    <Icon
                      as={posto.sugestao?.mesmaBr ? IconRoad : IconArrowsRightLeft}
                      boxSize="14px"
                      mt="2px"
                      flexShrink={0}
                      color={posto.sugestao?.mesmaBr ? "states.success.base" : "neutrals.text.sub-600"}
                    />
                    <Box>
                      <Text fontSize="11px" color="neutrals.text.sub-600">
                        Trocar por {titulo(posto.sugestao?.razaoSocial) || "Posto Rede Frota"} ·{" "}
                        {int(posto.sugestao?.distanciaKm ?? 0)} km
                      </Text>
                      <Text
                        fontSize="11px"
                        fontWeight="600"
                        color={posto.sugestao?.mesmaBr ? "states.success.dark" : "neutrals.text.sub-600"}
                      >
                        {posto.sugestao?.mesmaBr
                          ? `Mesma rodovia${posto.sugestao?.brComum ? ` · BR-${posto.sugestao.brComum}` : ""}`
                          : "Outra rodovia"}
                      </Text>
                    </Box>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="8px" mt="10px">
                    <RefCell
                      label="Preço no novo Posto"
                      value={switchRef}
                      hint="preço praticado no posto indicado"
                      accent
                      delta={{ pago: posto.precoMedioPago, ref: switchRef }}
                    />
                    <RefCell label="Você paga hoje" value={posto.precoMedioPago} />
                    <RefCell label="Economia / L" value={posto.precoMedioPago - switchRef} accent hint="pago − indicado" />
                  </SimpleGrid>
                  {posto.sugestao?.padraoVigente != null && (
                    <Text fontSize="10px" color="neutrals.text.sub-600" mt="8px">
                      Preço padrão (balcão) do novo posto:{" "}
                      <strong>{formatCurrency(posto.sugestao.padraoVigente)}/L</strong> — o novo
                      preço acima é o menor preço VENDIDO (praticado) no posto indicado.
                    </Text>
                  )}
                </Box>
              ) : temVendas ? (
                /* CREDENCIADO: preços com venda no posto (por produto). */
                <Box mt="12px" borderRadius="8px" border="1px solid" borderColor="states.success.light" bg="states.success.lighter" px="12px" py="10px">
                  <Text fontSize="12px" fontWeight="700" color="states.success.dark">
                    Preços com venda no Posto
                  </Text>
                  <Text fontSize="10px" color="neutrals.text.sub-600">
                    Preços usados em alguma transação no posto para o período de comparação
                  </Text>
                  {(posto.porProduto?.length
                    ? posto.porProduto
                    : ([
                        {
                          produto: "",
                          litros: posto.litros,
                          precoMedioPago: posto.precoMedioPago,
                          menorPrecoMedioDiario: posto.menorPrecoMedioDiario ?? posto.menorPrecoUsado,
                          medioPrecoUsado: posto.medioPrecoUsado,
                          maiorPrecoUsado: posto.maiorPrecoUsado,
                          economiaTotal: posto.economiaTotal,
                        },
                      ] as ProdutoBreakdown[])
                  ).map((bd) => (
                    <Box key={bd.produto || "single"} mt="10px">
                      <HStack spacing="6px" mb="6px">
                        {bd.produto ? <ChipCombustivel produto={bd.produto} /> : null}
                        <Text fontSize="10px" color="neutrals.text.sub-600">
                          no período · {num2(bd.litros)} L
                        </Text>
                      </HStack>
                      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="8px">
                        <RefCell
                          label="Menor preço médio"
                          value={bd.menorPrecoMedioDiario}
                          hint="média dos menores diários"
                          accent
                          delta={{ pago: bd.precoMedioPago, ref: bd.menorPrecoMedioDiario }}
                        />
                        <RefCell label="Preço médio" value={bd.medioPrecoUsado} delta={{ pago: bd.precoMedioPago, ref: bd.medioPrecoUsado }} />
                        <RefCell label="Maior preço" value={bd.maiorPrecoUsado} delta={{ pago: bd.precoMedioPago, ref: bd.maiorPrecoUsado }} />
                      </SimpleGrid>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
          )}

          {/* Tabela por período: pago × referência, cronológica. Credenciado = menor do
              posto (régua diária, por produto). Não credenciado = preço do indicado. */}
          <Text fontSize="12px" color="neutrals.text.sub-600" mb="12px">
            Por período (ordem cronológica): o que o cliente pagou e{" "}
            {isSwitch ? "o preço no posto indicado" : "o menor preço do posto naquele período"} — uma
            linha por preço.
          </Text>
          {(() => {
            // Produto dominante do posto (p/ o chip na tabela de troca, que é blended).
            const prodDominante = posto?.porProduto?.length
              ? [...posto.porProduto].sort((a, b) => b.litros - a.litros)[0]?.produto
              : undefined;
            const grupos: PeriodoGrupo[] =
              isSwitch && switchRef != null
                ? posto?.composicaoPeriodo?.length
                  ? [
                      {
                        produto: prodDominante,
                        media: posto.precoMedioPago ?? 0,
                        itens: posto.composicaoPeriodo.map((c) => ({ ...c, menorDoPosto: switchRef })),
                      },
                    ]
                  : []
                : posto?.porProduto?.length
                  ? posto.porProduto
                      .map((bd) => ({ produto: bd.produto, media: bd.precoMedioPago, itens: bd.composicaoPeriodo ?? [] }))
                      .filter((g) => g.itens.length)
                  : posto?.composicaoPeriodo?.length
                    ? [{ media: posto.precoMedioPago ?? 0, itens: posto.composicaoPeriodo }]
                    : [];
            return grupos.length ? (
              <TabelaPorPeriodo grupos={grupos} />
            ) : (
              <TabelaPagoSimples itens={pago} media={posto?.precoMedioPago ?? 0} />
            );
          })()}

          <Text fontSize="10px" color="neutrals.text.soft-400" mt="8px">
            {isSwitch ? (
              <>
                <strong>Menor preço</strong> = preço praticado no posto indicado mais próximo.{" "}
                <strong>Economia c/ menor preço</strong> = valor pago − preço do indicado, por litro.{" "}
                <strong>Economia total</strong> = economia por litro × litros do período.
              </>
            ) : (
              <>
                <strong>Menor preço</strong> = menor preço com venda no posto naquele período (régua
                diária — um preço que já vendeu, prova de que dá para pagar).{" "}
                <strong>Economia c/ menor preço</strong> = valor pago − menor preço, por litro.{" "}
                <strong>Economia total</strong> = economia por litro × litros do período.
              </>
            )}
          </Text>

          {!isSwitch && !temVendas && posto && (
            <Text fontSize="12px" color="neutrals.text.sub-600" mt="12px">
              Posto sem cobertura Rede Frota e sem indicação mais barata por perto — não há
              referência de economia para este posto neste período.
            </Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// Um grupo (produto) da tabela por período. Sem `produto` = modo single (sem coluna
// Combustível, fallback dos estudos antigos).
type PeriodoGrupo = { produto?: string; media: number; itens: ComposicaoPeriodoItem[] };
const econLitroP = (c: ComposicaoPeriodoItem) => (c.menorDoPosto != null ? Math.max(0, c.pago - c.menorDoPosto) : 0);
const aggPeriodo = (its: ComposicaoPeriodoItem[]) => {
  const litros = its.reduce((s, c) => s + c.litros, 0);
  const menorPond = litros > 0 ? its.reduce((s, c) => s + (c.menorDoPosto ?? 0) * c.litros, 0) / litros : null;
  const econTotal = its.reduce((s, c) => s + econLitroP(c) * c.litros, 0);
  return { litros, menorPond, econTotal };
};

// Tabela ÚNICA por período (régua diária). Linhas MISTURADAS por ordem cronológica; a
// coluna Combustível (depois do Período) marca o produto — aparece sempre que há produto
// identificado (inclusive 1 só). Subtotais por produto + total geral no FIM; subtotais só
// quando há 2+ produtos.
const TabelaPorPeriodo: FC<{ grupos: PeriodoGrupo[] }> = ({ grupos }) => {
  const multi = grupos.length > 1;
  // Coluna Combustível aparece sempre que há produto identificado (inclusive 1 produto,
  // ex.: planilha só S-10). Subtotais por produto seguem só no multi (2+ produtos).
  const temComb = grupos.some((g) => g.produto);
  const all = grupos.flatMap((g) => g.itens);
  const grand = aggPeriodo(all);
  const grandPago = grand.litros > 0 ? all.reduce((s, c) => s + c.pago * c.litros, 0) / grand.litros : 0;
  // Todas as linhas, tagueadas com o produto, ordenadas cronologicamente (dataMin ISO).
  const linhas = grupos
    .flatMap((g) => g.itens.map((c) => ({ c, produto: g.produto })))
    .sort((a, b) => (a.c.dataMin ?? "").localeCompare(b.c.dataMin ?? ""));
  return (
    <TableContainer borderRadius="8px" border="1px solid" borderColor="neutrals.stroke.soft-200">
      <Table variant="simple" size="sm">
        <Thead bg="neutrals.background.weak-50">
          <Tr>
            <Th {...th}>Período</Th>
            {temComb && <Th {...th}>Combustível</Th>}
            <Th {...th} isNumeric>Litros (L)</Th>
            <Th {...th} isNumeric>%</Th>
            <Th {...th} isNumeric>Valor Pago (R$/L)</Th>
            <Th {...th} isNumeric>Menor Preço (R$/L)</Th>
            <Th {...th} isNumeric>Economia c/ menor preço (R$/L)</Th>
            <Th {...th} isNumeric>Economia total (R$)</Th>
          </Tr>
        </Thead>
        <Tbody>
          {linhas.map(({ c, produto }, i) => (
            <Tr key={i}>
              <Td {...td}>{periodo(c.dataMin, c.dataMax)}</Td>
              {temComb && <Td {...td}>{produto ? <ChipCombustivel produto={produto} /> : ""}</Td>}
              <Td {...td} isNumeric>{num2(c.litros)}</Td>
              <Td {...td} isNumeric>{pctNum(c.percentual)}</Td>
              <Td {...td} isNumeric>{num2(c.pago)}</Td>
              <Td {...td} isNumeric color="states.success.dark" fontWeight="600">
                {c.menorDoPosto != null ? num2(c.menorDoPosto) : "—"}
              </Td>
              <Td {...td} isNumeric>{c.menorDoPosto != null ? num2(econLitroP(c)) : "—"}</Td>
              <Td {...td} isNumeric fontWeight="600" color="states.success.dark">
                {c.menorDoPosto != null ? num2(econLitroP(c) * c.litros) : "—"}
              </Td>
            </Tr>
          ))}
        </Tbody>
        <Tfoot>
          {/* Subtotais por produto (só multi) — no fim, antes do total geral. */}
          {multi &&
            grupos.map((g, gi) => {
              const ag = aggPeriodo(g.itens);
              return (
                <Tr key={gi} bg="neutrals.background.weak-50" sx={gi === 0 ? { "& > th": { borderTop: "2px solid", borderColor: "neutrals.stroke.soft-200" } } : undefined}>
                  <Th {...th} color="neutrals.text.strong-950">Subtotal</Th>
                  <Th {...th}>{g.produto ? <ChipCombustivel produto={g.produto} /> : ""}</Th>
                  <Th {...th} isNumeric color="neutrals.text.strong-950">{num2(ag.litros)}</Th>
                  <Th {...th} isNumeric color="neutrals.text.strong-950">100</Th>
                  <Th {...th} isNumeric color="neutrals.text.strong-950">{num2(g.media)}</Th>
                  <Th {...th} isNumeric color="states.success.dark">{ag.menorPond != null ? num2(ag.menorPond) : "—"}</Th>
                  <Th {...th} isNumeric color="neutrals.text.strong-950">{ag.litros > 0 ? num2(ag.econTotal / ag.litros) : "—"}</Th>
                  <Th {...th} isNumeric color="states.success.dark">{num2(ag.econTotal)}</Th>
                </Tr>
              );
            })}
          <Tr bg="neutrals.background.weak-50" sx={{ "& > th": { borderTop: "2px solid", borderColor: "neutrals.stroke.soft-200", fontWeight: 700 } }}>
            <Th {...th} color="neutrals.text.strong-950">{multi ? "Total geral" : "Total / média"}</Th>
            {temComb && <Th />}
            <Th {...th} isNumeric color="neutrals.text.strong-950">{num2(grand.litros)}</Th>
            <Th />
            <Th {...th} isNumeric color="neutrals.text.strong-950">{num2(grandPago)}</Th>
            <Th {...th} isNumeric color="states.success.dark">{grand.menorPond != null ? num2(grand.menorPond) : "—"}</Th>
            <Th {...th} isNumeric color="neutrals.text.strong-950">{grand.litros > 0 ? num2(grand.econTotal / grand.litros) : "—"}</Th>
            <Th {...th} isNumeric color="states.success.dark">{num2(grand.econTotal)}</Th>
          </Tr>
        </Tfoot>
      </Table>
    </TableContainer>
  );
};

// Composição do que o cliente pagou, em tabela única.
const TabelaPagoSimples: FC<{ itens: ComposicaoPagoItem[]; media: number }> = ({ itens, media }) => (
  <TableContainer borderRadius="8px" border="1px solid" borderColor="neutrals.stroke.soft-200">
    <Table variant="simple" size="sm">
      <Thead bg="neutrals.background.weak-50">
        <Tr>
          <Th {...th}>Período</Th>
          <Th {...th} isNumeric>Litros</Th>
          <Th {...th} isNumeric>%</Th>
          <Th {...th} isNumeric>Pago</Th>
        </Tr>
      </Thead>
      <Tbody>
        {itens.map((c, i) => (
          <Tr key={i}>
            <Td {...td}>{periodo(c.dataMin, c.dataMax)}</Td>
            <Td {...td} isNumeric>{num2(c.litros)}</Td>
            <Td {...td} isNumeric>{pct(c.percentual)}</Td>
            <Td {...td} isNumeric>{formatCurrency(c.preco)}</Td>
          </Tr>
        ))}
      </Tbody>
      <Tfoot>
        <Tr bg="neutrals.background.weak-50" sx={{ "& > th": { borderTop: "2px solid", borderColor: "neutrals.stroke.soft-200" } }}>
          <Th {...th} color="neutrals.text.strong-950">Total / média</Th>
          <Th {...th} isNumeric color="neutrals.text.strong-950">
            {num2(itens.reduce((s, c) => s + c.litros, 0))}
          </Th>
          <Th />
          <Th {...th} isNumeric color="neutrals.text.strong-950">{formatCurrency(media)}</Th>
        </Tr>
      </Tfoot>
    </Table>
  </TableContainer>
);
